import Logo from './Logo';
import { IconX } from '@tabler/icons-react';

// ─── Standardized bottom-bar building blocks ────────────────────────────────────
// Desktop blocks are rounded and sit an even BAR_GAP in from the bar's edges and from each other (the header ✕ too). Callers'
// own tool buttons get the radius / spacing from the `.sk-bar-tools` rule in global.scss, whatever inline style they carry.
const BAR_GAP = 8;
const BLOCK_RADIUS = 10;
// One source of truth for the editor modals' bottom bar so every modal (upload crop,
// result crop, custom download…) has the SAME bar frame and button blocks. Features
// compose these with their own handlers/content.

/** The bar frame. Desktop/compact: fixed-height strip with absolute blocks inside.
 *  Mobile: padded column (feature provides its own stacked content). */
export function EditorBarFrame({ isMobile = false, compact = false, children }) {
    return (
        <div
            className="relative shrink-0 border-t border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900"
            style={isMobile ? { padding: '14px 16px 24px' } : { height: compact ? 52 : 60 }}
        >
            {children}
        </div>
    );
}

/** Left-pinned cluster of tool blocks (zoom / rotate / mirror…). Desktop only. */
export function EditorBarTools({ children }) {
    return (
        <div className="sk-bar-tools" style={{ position: 'absolute', top: BAR_GAP, bottom: BAR_GAP, left: BAR_GAP, display: 'flex', alignItems: 'stretch', gap: BAR_GAP, flexWrap: 'nowrap' }}>
            {children}
        </div>
    );
}

/** Neutral full-height block placed just left of the primary (cancel / skip). */
export function EditorBarSecondary({ compact = false, onClick, children, title }) {
    return (
        <button
            className="inline-flex items-center gap-2 font-semibold text-sm bg-zinc-100 dark:bg-zinc-800 hover:bg-zinc-200 dark:hover:bg-zinc-700 text-zinc-700 dark:text-zinc-200 transition-colors"
            style={{ position: 'absolute', top: BAR_GAP, bottom: BAR_GAP, right: (compact ? 160 : 200) + 2 * BAR_GAP, width: compact ? 150 : 178, justifyContent: 'center', border: 'none', borderRadius: BLOCK_RADIUS, cursor: 'pointer' }}
            onClick={onClick}
            title={title}
        >
            {children}
        </button>
    );
}

/** Primary orange-gradient block pinned to the bottom-right edge. `loading` = its action is running: the block keeps its full
 *  colour (`disabled` dims it), ignores clicks and shows a busy cursor — the caller puts the spinner + a "working" label in
 *  its children (see CropStage). */
export function EditorBarPrimary({ compact = false, onClick, disabled = false, loading = false, children }) {
    return (
        <button
            className={`inline-flex items-center gap-2 font-bold text-sm text-white ${loading ? '' : 'disabled:opacity-60'}`}
            style={{ position: 'absolute', top: BAR_GAP, right: BAR_GAP, bottom: BAR_GAP, width: compact ? 160 : 200, justifyContent: 'center', background: 'linear-gradient(135deg,#FBA53A,#E68A00)', border: 'none', borderRadius: BLOCK_RADIUS, cursor: loading ? 'progress' : disabled ? 'not-allowed' : 'pointer' }}
            onClick={onClick}
            disabled={disabled || loading}
            aria-busy={loading || undefined}
        >
            {children}
        </button>
    );
}

const TOOL_TONES = { emerald: '#059669', indigo: '#6366f1', teal: '#0d9488', brand: '#E68A00' };

/** One full-height coloured block of the bar's tool cluster (zoom −/+, rotate, fit…). Icon-only by default (fixed width);
 *  `wide` = an icon + label block with padding, `gap` = the space to its left neighbour. */
export function EditorBarToolButton({ tone = 'emerald', wide = false, compact = false, gap = 0, onClick, disabled = false, title, children, ...rest }) {
    return (
        <button
            type="button"
            onClick={onClick}
            disabled={disabled}
            title={title}
            className={`inline-flex items-center ${wide ? 'gap-1.5 font-semibold text-sm' : 'justify-center'} disabled:opacity-60`}
            style={{
                ...(wide ? { marginLeft: gap, padding: '0 18px' } : { width: compact ? 56 : 64 }),
                background: TOOL_TONES[tone] || tone, color: '#fff', border: 'none', borderRadius: BLOCK_RADIUS, cursor: disabled ? 'not-allowed' : 'pointer',
            }}
            {...rest}
        >
            {children}
        </button>
    );
}

/**
 * Shared DESIGN shell for the full-screen image editor modals (upload crop, result
 * crop, custom download…). Owns only the look: logo header with a full-height red
 * close button, black canvas area, and optional slots. All FUNCTIONALITY stays in the
 * caller and is passed in dynamically:
 *
 *   icon        — header icon chip element (hidden on mobile/compact)
 *   headerExtra — element beside the title (ratio chips, image counter…)
 *   canvas      — content of the black canvas area (cropper, preview canvas…)
 *   canvasRef   — optional ref for the canvas container (e.g. wheel-zoom listener)
 *   statsBar    — optional strip under the canvas (left column)
 *   sidebar     — optional right options sidebar (fixed 400px on desktop — `sidebarWidth` overrides it; the caller
 *                 renders its own scroll area + footer inside)
 *   sidebarDock — on a phone the sidebar only takes its own height (a slim dock), leaving the rest to the photo
 *   bare        — sits inside a dark page area instead of a window: see-through, just the title and close button, a slimmer canvas padding
 *   bottomBar   — optional full-width bottom action bar (crop toolbar style)
 *   leftStyle   — optional style override for the left (canvas) column
 *   dragHandle  — show the mobile sheet drag handle above the header
 *
 * The shell fills its parent (Mantine Modal / shadcn DialogContent must be a
 * full-height flex column).
 */
export default function EditorModalShell({
    isMobile = false,
    compact = false,
    icon = null,
    title,
    headerExtra = null,
    onClose,
    canvas,
    canvasRef,
    statsBar = null,
    sidebar = null,
    sidebarWidth = 400,
    sidebarDock = false,
    bare = false,
    bottomBar = null,
    leftStyle,
    dragHandle = false,
}) {
    return (
        <div className={`flex flex-col h-full min-h-0 flex-1 ${bare ? '' : 'bg-white dark:bg-zinc-900'}`}>
            {dragHandle && isMobile && (
                <div className="flex justify-center pt-2.5 pb-1 shrink-0">
                    <div className="w-10 h-1 rounded-full bg-zinc-300 dark:bg-zinc-600" />
                </div>
            )}

            {/* ── Header ── */}
            <div
                className={`relative flex items-center gap-3 shrink-0 ${bare ? '' : 'border-b border-zinc-200 dark:border-zinc-700'}`}
                style={{ paddingTop: isMobile ? 12 : 9, paddingBottom: isMobile ? 12 : 9, paddingLeft: isMobile ? 14 : compact ? 16 : 22, paddingRight: isMobile || compact ? 58 : 80 }}
            >
                <div className="flex items-center min-w-0" style={{ gap: compact ? 10 : 12 }}>
                    {!bare && <Logo w={isMobile ? 92 : compact ? 100 : 118} />}
                    {!bare && <div className="w-px bg-zinc-200 dark:bg-zinc-700 shrink-0" style={{ height: compact ? 24 : 30 }} />}
                    {!isMobile && !compact && icon && (
                        <span className="inline-flex items-center justify-center w-[38px] h-[38px] rounded-[11px] bg-primary/10 text-primary shrink-0">
                            {icon}
                        </span>
                    )}
                    <h3 className={`m-0 font-bold truncate ${bare ? 'text-white' : 'text-zinc-800 dark:text-zinc-100'} ${isMobile || compact ? 'text-[15px]' : 'text-base'}`} style={{ lineHeight: 1.25 }}>
                        {title}
                    </h3>
                </div>
                {headerExtra}
                <button
                    onClick={onClose}
                    aria-label="Close"
                    className="absolute flex items-center justify-center bg-red-500 hover:bg-red-600 text-white transition-colors"
                    style={{ top: BAR_GAP, bottom: BAR_GAP, right: BAR_GAP, aspectRatio: '1 / 1', borderRadius: BLOCK_RADIUS, border: 'none', cursor: 'pointer' }}
                >
                    <IconX size={isMobile || compact ? 18 : 22} strokeWidth={2.5} />
                </button>
            </div>

            {/* ── Body: black canvas column + optional right sidebar ── */}
            <div className={`flex flex-1 min-h-0 overflow-hidden ${isMobile ? 'flex-col' : 'flex-row'}`}>
                <div className="flex flex-col flex-1 min-w-0 min-h-0" style={leftStyle}>
                    <div
                        ref={canvasRef}
                        className={`flex-1 min-h-0 flex items-center justify-center overflow-hidden relative ${bare ? '' : 'bg-black'}`}
                        style={{ padding: bare ? '4px 12px 10px' : isMobile ? '20px 14px' : 24 }}
                    >
                        {canvas}
                    </div>
                    {statsBar}
                </div>
                {sidebar && (
                    <div className={`flex flex-col min-h-0 ${bare ? '' : 'bg-white dark:bg-zinc-900'} ${isMobile ? (sidebarDock ? 'w-full shrink-0' : 'w-full flex-1') : 'shrink-0 border-l border-zinc-200 dark:border-zinc-700'}`} style={isMobile ? undefined : { width: sidebarWidth }}>
                        {sidebar}
                    </div>
                )}
            </div>

            {bottomBar}
        </div>
    );
}
